import { useCallback, useMemo, useState } from 'react';

import { BridgeEventMethod, FullMessage, SimpleMessage } from '@scaleaq/sitecontrol-lib';

import { useEventSubscription } from '../internal';
import { useBridgeRegistration } from '../registerAndSubscribe';

export const useBridgeEvents = <T, V>(
  eventNames: string[],
  selector: (id: string, eventName: string, data?: T) => V,
  useSimpleMessage: boolean = false
) => {
  const [result, setResult] = useState<{ [key: string]: V }>({});
  const listenerId = useMemo(() => `useBridgeEvents-${eventNames.join('-')}`, [eventNames]);
  const callback = useCallback(
    (message: FullMessage | SimpleMessage) => {
      const data = Object.prototype.hasOwnProperty.call(message, 'data')
        ? (message as FullMessage).data
        : undefined;

      const { id, eventName, listenerIds } = message;
      if (listenerIds && !listenerIds?.includes(listenerId)) {
        return;
      }
      if (eventNames.find(ev => eventName.indexOf(ev) !== -1)) {
        setResult(prevState => {
          return {
            ...prevState,
            [eventName]: selector(id, eventName, data as T),
          };
        });
      }
    },
    [eventNames, listenerId, selector]
  );

  // Always, first register the event handler
  useBridgeRegistration({
    bridgeEventMethod: useSimpleMessage
      ? BridgeEventMethod.SimpleMessage
      : BridgeEventMethod.FullMessage,
    eventCallback: callback,
  });

  const { isLoading, isError } = useEventSubscription({ eventNames, listenerId });

  return { result, isLoading, isError };
};
